import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

type ToastKind = 'success' | 'error' | 'info';
type Toast = { id: number; kind: ToastKind; text: string };

type ToastApi = {
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const STYLES: Record<ToastKind, { cls: string; Icon: typeof Info }> = {
  success: { cls: 'border-ok/25 bg-ok-soft text-ok', Icon: CheckCircle2 },
  error: { cls: 'border-danger/25 bg-danger-soft text-danger', Icon: AlertCircle },
  info: { cls: 'border-info/25 bg-info-soft text-info', Icon: Info },
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, kind, text }]);
      setTimeout(() => remove(id), kind === 'error' ? 6000 : 3500);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (t) => push('success', t),
      error: (t) => push('error', t),
      info: (t) => push('info', t),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => {
          const { cls, Icon } = STYLES[t.kind];
          return (
            <div
              key={t.id}
              role="status"
              className={clsx(
                'pointer-events-auto flex items-start gap-2.5 rounded-lg border px-3.5 py-3 text-[13px] shadow-md',
                cls,
              )}
            >
              <Icon className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 flex-1">{t.text}</span>
              <button
                onClick={() => remove(t.id)}
                aria-label="დახურვა"
                className="-mr-1 rounded p-0.5 opacity-60 transition hover:opacity-100"
              >
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast უნდა გამოიყენებოდეს <ToastProvider> შიგნით');
  return ctx;
}
